import { useEffect, useRef } from "react";
import {
  useContentShareState,
  useLocalVideo,
  useMeetingManager,
} from "amazon-chime-sdk-component-library-react";
import { Layout } from "@core/features/video";
import type { VideoSourceState } from "@core/features/video";
import { usePlatformStore, useVideoConfig } from "@platform/store";
import type { ChimeObserverState } from "./chimeObserverState";

/**
 * Syncs local video state (`isVideoEnabled`, `isLocalUserSharing`,
 * `sharingAttendeeId`) into Zustand and updates downlink preferences via
 * the state machine. Auto-switches to Featured layout on content share start.
 *
 * Both share-start and layout-flip preference updates route through
 * `state.applyPreferenceUpdate({ bypassHysteresis: true })`. Bypass is
 * justified: these are user-driven / share-driven discrete transitions
 * that must propagate immediately. Active-speaker churn (the noisy caller)
 * is the one subject to hysteresis, in `useActiveSpeakerDownlinkSync`.
 *
 * Share-start triggers preemptive mode for 5 s (the state machine handles
 * the timer). Note the SDK constraint: `ContentShareObserver.contentShareDidStart`
 * is sharer-only, so the share-start trigger lives here on
 * `useContentShareState().sharingAttendeeId` — which transitions for
 * sharers AND viewers via the component library's roster-based detection.
 */
export function useLocalVideoSync(state: ChimeObserverState) {
  const meetingManager = useMeetingManager();
  const { isVideoEnabled } = useLocalVideo();
  const { isLocalUserSharing, sharingAttendeeId } = useContentShareState();
  const { layout, setLayout } = useVideoConfig();
  const prevSharingAttendeeIdRef = useRef<string | null>(null);

  // Sync local video / content share into Zustand + notify state machine of
  // share start/stop transitions. Final step: trigger a preference update
  // that consults the (possibly-just-set) preemptive mode.
  useEffect(() => {
    const s = usePlatformStore.getState();

    const nextVideoSourceState: VideoSourceState = {
      ...s.videoSourceState,
      hasLocalVideo: isVideoEnabled,
      hasLocalContentSharing: isLocalUserSharing,
      contentShareId: sharingAttendeeId,
    };

    s.setVideoSourceState(nextVideoSourceState);

    // Detect share start/stop transitions on `sharingAttendeeId`. This effect
    // fires on both sharers and viewers (the component library updates
    // `sharingAttendeeId` from videoTileDidUpdate on every connected client).
    const prev = prevSharingAttendeeIdRef.current;
    prevSharingAttendeeIdRef.current = sharingAttendeeId;
    if (!prev && sharingAttendeeId) {
      state.notifyShareStart();
    } else if (prev && !sharingAttendeeId) {
      state.notifyShareStop();
    }

    state.applyPreferenceUpdate({ bypassHysteresis: true });
  }, [isLocalUserSharing, isVideoEnabled, meetingManager.meetingSession, sharingAttendeeId, state]);

  // Sync layout changes into downlink preferences. Currently only the auto-
  // flip below triggers layout changes (no user-initiated layout toggle
  // exists in the codebase), but routing through the state machine is the
  // right shape if a manual toggle is added later.
  useEffect(() => {
    state.applyPreferenceUpdate({ bypassHysteresis: true });
  }, [layout, state]);

  // Auto-switch to Featured layout on content share
  useEffect(() => {
    if (sharingAttendeeId && layout === Layout.Gallery) {
      setLayout(Layout.Featured);
    }
  }, [sharingAttendeeId, layout, setLayout]);
}
